import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Typography, Spin, Dropdown, Button } from 'antd';
import { CustomPageHeader, CustomTable, useText } from '@src/components';
import { useLang } from '@src/context';
import { formatPlate } from '@src/utils/utils';
import '@src/styles/errorList/errorList.scss';
import {
  getTicketCarProviderLogs,
  updateTicketCarProviderLogs,
} from '@src/store/slice/errorListStore/errorListSlice';
import dayjs from '@src/utils/day';
import CustomImage from '@src/components/CustomImage';
import iconSetting from '@src/assets/icons/icon-settings.svg';
import filterButton from '@src/assets/icons/icon-task-square.svg';
import CustomIconComponent from '@src/components/CustomIconComponent';
import CustomFilterCollapse from '@src/components/CustomFilterCollapse';

const { Text } = Typography;

const ErrorList = (props) => {
  const dispatch = useDispatch();
  const { language } = useLang();
  const {
    ticketCarProviderLogsResponseItemList = [],
    operationList = null,
    providerList = null,
    statusList = null,
    plateList = null,
    customerList = null,
    fleetList = null,
    loading,
    pagedProperty,
  } = useSelector((state) => state.errorList);

  const [checkedProviders, setCheckedProviders] = useState([]);
  const [checkedStatus, setCheckedStatus] = useState([]);
  const [checkedOperations, setCheckedOperations] = useState([]);
  const [operations, setOperations] = useState(null);
  const [status, setStatus] = useState(null);
  const [providers, setProviders] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [filteredPlate, setFilteredPlate] = useState('');
  const [filteredCustomerId, setFilteredCustomerId] = useState(0);
  const [filteredFleetId, setFilteredFleetId] = useState(0);
  const [filteredDate, setFilteredDate] = useState('');
  const [requestFilter, setRequestFilter] = useState(null);

  const providerText = useText('provider');
  const errorTypeText = useText('errorType');
  const status3Text = useText('status3');
  const transactionDate3Text = useText('transactionDate3');
  const licensePlate3Text = useText('licensePlate3');
  const customerCodeText = useText('customerCode');
  const fleetCodeText = useText('fleetCode');

  // Panels dizisini state + useEffect yerine doğrudan render sırasında türetiyoruz
  const panels = [
    {
      list: checkedProviders,
      setList: setCheckedProviders,
      text: providerText,
      initialList: providers,
      key: 'providers',
    },
    {
      list: checkedOperations,
      setList: setCheckedOperations,
      text: errorTypeText,
      initialList: operations,
      key: 'operations',
    },
    {
      list: checkedStatus,
      setList: setCheckedStatus,
      text: status3Text,
      initialList: status,
      key: 'status',
    },
    {
      setList: setFilteredDate,
      text: transactionDate3Text,
      key: 'operationDate',
      date: true,
      defaultValue: filteredDate,
    },
    {
      setList: setFilteredPlate,
      text: licensePlate3Text,
      initialList: plateList,
      key: 'plateList',
      input: true,
      defaultValue: filteredPlate,
    },
    {
      setList: setFilteredCustomerId,
      text: customerCodeText,
      initialList: customerList,
      key: 'customerList',
      input: true,
      defaultValue: filteredCustomerId,
    },
    {
      setList: setFilteredFleetId,
      text: fleetCodeText,
      initialList: fleetList,
      key: 'fleetList',
      input: true,
      defaultValue: filteredFleetId,
    },
  ];

  const fetchVehicleListData = async () => {
    let req = { pageNumber: pagination.current, pageSize: pagination.pageSize };
    if (requestFilter) {
      req.filter = requestFilter;
    }
    await dispatch(getTicketCarProviderLogs(req));
  };

  useEffect(() => {
    fetchVehicleListData();
  }, [dispatch, pagination, language]);

  useEffect(() => {
    try {
      if (statusList) {
        setStatus(
          Object.entries(statusList).map(([key, value]) => ({
            key: key,
            value: value,
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, [statusList]);

  useEffect(() => {
    try {
      if (operationList) {
        setOperations(
          Object.entries(operationList).map(([key, value]) => ({
            key: key,
            value: value,
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, [operationList]);

  useEffect(() => {
    try {
      if (providerList) {
        setProviders(
          Object.entries(providerList).map(([key, value]) => ({
            key: key,
            value: value,
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, [providerList]);

  const clickUpdateTicketCarProviderLogs = async (record) => {
    await dispatch(updateTicketCarProviderLogs({ ids: [record.id] })).unwrap();
    fetchVehicleListData();
  };

  const columns = [
    {
      title: useText('operationCode'),
      dataIndex: 'id',
      key: 'id',
      fixed: 'left',
      align: 'center',
      width: 80,
      height: 50,
      className: 'custom-id-column',
    },
    {
      title: useText('licensePlate'),
      dataIndex: 'plate',
      key: 'plate',
      align: 'left',
      width: 80,
      height: 50,
      className: 'custom-plate-column',
      render: (text) => formatPlate(text),
    },
    {
      title: useText('customerCode3'),
      dataIndex: 'customerId',
      key: 'customerId',
      align: 'left',
      width: 80,
      height: 50,
    },
    {
      title: useText('fleetCode2'),
      dataIndex: 'externalFleetId',
      key: 'externalFleetId',
      align: 'left',
      width: 80,
      height: 50,
    },
    {
      title: <Text t="provider2" />,
      dataIndex: 'provider',
      key: 'provider',
      align: 'left',
      width: 60,
      height: 50,
      render: (_, record) => {
        const vouId = record.providerType;
        const IconComponent = CustomIconComponent({ providerType: vouId });
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {IconComponent && (
              <img src={IconComponent} alt={record.provider} style={{ width: 24, height: 24 }} />
            )}
          </div>
        );
      },
    },
    {
      title: useText('transactionType2'),
      dataIndex: 'operation',
      key: 'operation',
      align: 'left',
      width: 90,
      height: 50,
    },
    {
      title: useText('errorMessage'),
      dataIndex: 'message',
      key: 'message',
      align: 'left',
      width: 90,
      height: 50,
    },
    {
      title: <Text t="transactionDate2" />,
      dataIndex: 'insertTime',
      key: 'insertTime',
      align: 'left',
      width: 80,
      height: 50,
      render: (insertTime) => dayjs(insertTime).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: useText('status'),
      dataIndex: 'status',
      key: 'status',
      align: 'left',
      width: 70,
      height: 50,
      render: (status) => (
        <button
          className={
            status === 'Bekliyor'
              ? 'top-filter-head-def-bc top-filter-head-waiting-bc'
              : 'top-filter-head-def-bc top-filter-head-done-bc'
          }
          style={{ cursor: 'default' }}
        >
          <p
            className="top-filter-head-def-t"
            style={{ color: status === 'Bekliyor' ? '#c3410d' : '#027a48' }}
          >
            {status}
          </p>
        </button>
      ),
    },
    {
      title: useText('actionUpperCase'),
      dataIndex: 'resolved',
      key: 'resolved',
      align: 'left',
      width: 80,
      height: 50,
      render: (_, record) => {
        if (record.resolved) {
          return null;
        }
        return (
          <button
            className="error-list-state-button"
            onClick={() => clickUpdateTicketCarProviderLogs(record)}
          >
            <p className="top-filter-head-def-t">Tamamlandı</p>
          </button>
        );
      },
    },
  ];

  const handlePageChange = (page, pageSize) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current: page?.current,
    }));
  };

  const clearDemands = () => {
    try {
      setRequestFilter(null);
      setCheckedOperations([]);
      setCheckedProviders([]);
      setCheckedStatus([]);
      setFilteredCustomerId(0);
      setFilteredDate('');
      setFilteredFleetId(0);
      setFilteredPlate('');
      setRequestFilter(null);
      fetchVehicleListData();
    } catch (error) {
      console.error(error);
    }
  };

  const clickFilter = async (tempFilter = null) => {
    try {
      const filter = tempFilter?.plate
        ? tempFilter
        : {
            operationIds: checkedOperations?.map((it) => parseInt(it, 10)),
            providerIds: checkedProviders?.map((it) => parseInt(it, 10)),
            statusIds: checkedStatus?.map((it) => parseInt(it, 10)),
            startTime: filteredDate?.start,
            endTime: filteredDate?.end,
            plate: filteredPlate,
            customerId: filteredCustomerId === 0 ? null : filteredCustomerId,
            fleetId: filteredFleetId === 0 ? null : filteredFleetId,
          };
      setRequestFilter(filter);
      await dispatch(
        getTicketCarProviderLogs({ pageNumber: 1, pageSize: pagination.pageSize, filter }),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const listenClickCheckbox = (setList, tempList) => {
    setList(tempList);
  };

  function FilterMenu() {
    return (
      <div className="custom-filter-menu">
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span className="custom-filter-menu-filtrele">
            <Text t="filter2" />
          </span>
          <button onClick={clearDemands} className="custom-filter-menu-clear">
            <Text t="clean" />
          </button>
        </div>

        <CustomFilterCollapse panels={panels} listenClickCheckbox={listenClickCheckbox} />

        <Button
          type="primary"
          onClick={clickFilter}
          block
          className="custom-filter-menu-filter-button"
        >
          <CustomImage src={filterButton} /> <Text t="filter2" />
        </Button>
      </div>
    );
  }

  const content = () => {
    return loading?.ticketCarProviderLogsResponseItemList ? (
      <div className="loading-container" style={{ textAlign: 'center', padding: '250px' }}>
        <Spin size="large" className="custom-spin" />
      </div>
    ) : (
      <Card
        title="Hata İşlemleri"
        className="error-list-balance-list-container"
        headStyle={{ fontFamily: 'UbuntuRegular', fontSize: '18px', fontWeight: 'bolder' }}
        extra={
          <div className="error-list-balance-tools">
            <div className="header-notification">
              <Dropdown overlay={<FilterMenu />} trigger={['click']}>
                <Button
                  type="text"
                  icon={<CustomImage src={iconSetting} />}
                  style={{
                    borderRadius: '0',
                    padding: '0',
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 'auto',
                    height: 'auto',
                  }}
                />
              </Dropdown>
            </div>
          </div>
        }
      >
        <CustomTable
          data-testid="table-test-id"
          pagination={
            pagedProperty?.totalItems < 11
              ? false
              : {
                  pageSize: pagedProperty?.pageSize,
                  total: pagedProperty?.totalItems,
                  current: pagedProperty?.currentPage,
                  className: 'error-list-pagination',
                }
          }
          onPageChange={handlePageChange}
          dataSource={ticketCarProviderLogsResponseItemList}
          columns={columns}
          className="babs-list-table"
          rowKey={(record) => `vehicle-managment-list-${record?.id}`}
        />
      </Card>
    );
  };

  return (
    <div data-testid="demands-test-id">
      <CustomPageHeader
        title={<Text>Hata İşlemleri</Text>}
        showBreadCrumb
        showHelpButton
        routes={['ticketCarHeader']}
      >
        {content()}
      </CustomPageHeader>
    </div>
  );
};

export default ErrorList;